import { useCallback } from 'react'
import { useLatest } from 'react-use'
import { ModelMeshClickListener, useModelClickListenerV2 } from '@scene'
import { isEqual } from 'lodash-es'
import { useControlStore } from '@editorStores'
import {
  MarqueeSelectionListener,
  useIfcFiles,
  useIfcSelection,
  useMarqueeSelectionListener,
  useUpdateIfcModelMetadata,
} from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'

interface Options {
  /**
   * Disable click/marquee element selection while keeping the hook mounted — e.g. the alignment
   * step turns selection off while the three alignment points are being picked in the scene.
   * Defaults to enabled.
   */
  enabled?: boolean
}

/**
 * Shared IFC-element selection wiring for the IFC Import drawer panels (filter, storey assignment,
 * …): enables click (multi-select modifier to add) and marquee selection of IFC elements, and
 * exposes the active file's metadata + the metadata updater. GLB visibility is controlled by the
 * page, not here, so switching between panels doesn't toggle the model on/off.
 */
const useIFCElementSelection = ({ enabled = true }: Options = {}) => {
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const { selectedIfcElementGuids, setSelectedIfcElementGuids } = useIfcSelection()
  const actionMode = useControlStore(state => state.actionMode)
  const selectionMode = useControlStore(state => state.selectionMode)

  const glbFiles = useIfcFiles()
  const activeGlbFile = activeIfcFileGuid
    ? glbFiles.find((file: FileItem) => file.guid === activeIfcFileGuid) ?? null
    : null
  const metadata = activeGlbFile?.additional_data?.model_metadata ?? null

  const { updateIfcModelMetadata } = useUpdateIfcModelMetadata(activeIfcFileGuid)

  // Read the current selection/mode through refs so the click and marquee listeners stay stable and
  // `useModelClickListenerV2` / `useMarqueeSelectionListener` don't re-subscribe after every update.
  const selectedIfcElementGuidsRef = useLatest(selectedIfcElementGuids)
  const selectionModeRef = useLatest(selectionMode)
  const enabledRef = useLatest(enabled)

  const onIfcElementClick = useCallback<ModelMeshClickListener>(
    (guid, event) => {
      const currentSelection = selectedIfcElementGuidsRef.current
      const multiSelect = eventMatchesPointerModifier(event?.nativeEvent, 'multiSelect')
      if (multiSelect) {
        setSelectedIfcElementGuids(
          currentSelection.includes(guid)
            ? currentSelection.filter(g => g !== guid)
            : [...currentSelection, guid],
        )
      } else {
        setSelectedIfcElementGuids(
          currentSelection.length === 1 && currentSelection[0] === guid ? [] : [guid],
        )
      }
    },
    [selectedIfcElementGuidsRef, setSelectedIfcElementGuids],
  )

  useModelClickListenerV2('ifc_glb_model', onIfcElementClick, {
    enabled: enabled && actionMode !== 'hide',
    guids: null,
    enableOnIfcMode: true,
    enableOnMultiSelect: true,
  })

  const onMarqueeSelect = useCallback<MarqueeSelectionListener>(
    (ids, objects) => {
      if (!enabledRef.current) return
      // Only marquee-select the IFC GLB model while "Import auswählen" mode is active.
      if (selectionModeRef.current !== 'ifc-selection') return
      const gltfIds = ids.filter((_id, index) => objects[index]?.userData?.type === 'gltf-model')
      if (isEqual(selectedIfcElementGuidsRef.current, gltfIds)) return
      setSelectedIfcElementGuids(gltfIds)
    },
    [enabledRef, selectionModeRef, selectedIfcElementGuidsRef, setSelectedIfcElementGuids],
  )

  useMarqueeSelectionListener(onMarqueeSelect)

  return {
    selectedIfcElementGuids,
    setSelectedIfcElementGuids,
    activeGlbFile,
    metadata,
    updateIfcModelMetadata,
  }
}

export default useIFCElementSelection
